import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, AlertCircle, Calendar } from "lucide-react";

export default function ProjectCard({ project, onClick }) {
  const statusConfig = {
    ASSIGNED: { label: "Assigned", className: "bg-[#fff2d8] text-[#9a6500]" },
    RECORDING: { label: "Recording", className: "bg-emerald-50 text-emerald-700" },
    SUBMITTED_FOR_QC: { label: "In QC", className: "bg-amber-100 text-amber-800" },
    QC_PASSED: { label: "QC Passed", className: "bg-green-100 text-green-800" },
    COMPLETED: { label: "Completed", className: "bg-green-100 text-green-800" },
  };

  const config = statusConfig[project.status] || {
    label: project.status,
    className: "bg-slate-100 text-slate-800",
  };

  return (
    <Card
      data-testid={`project-card-${project.id}`}
      className="cursor-pointer border border-[#eadfcd] bg-white/95 transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(94,64,18,0.12)] dark:border-[#4d3d16] dark:bg-[#11100d]/95 dark:hover:shadow-[0_18px_45px_rgba(0,0,0,0.48)]"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-black dark:text-[#f8efe0] text-lg truncate mb-1">
              {project.book_title || "Untitled Book"}
            </h4>

            <p className="text-sm text-slate-600 dark:text-[#9f947f] mb-3">
              {project.book_language || "English"} • {project.assignment_type}
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className={config.className}>{config.label}</Badge>

              {project.open_qc_count > 0 && (
                <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {project.open_qc_count} QC Issues
                </Badge>
              )}

              {project.due_date && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(project.due_date).toLocaleDateString()}
                </Badge>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-[#d5c8ae]">Progress</span>
                <span className="font-medium text-[#b77900]">
                  {project.recording_pct || 0}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[#f4ead8] dark:bg-[#2a2110]">
                <div
                  className="h-full bg-gradient-to-r from-[#b77900] to-[#f5cf75] transition-all"
                  style={{ width: `${project.recording_pct || 0}%` }}
                />
              </div>
            </div>

            {project.next_action && (
              <p className="mt-3 text-sm font-medium text-[#b77900]">
                Next: {project.next_action}
              </p>
            )}
          </div>

          <Button
            size="sm"
            className="shrink-0 bg-[#d99b13] text-black hover:bg-[#f0bd43]"
            data-testid={`open-project-${project.id}`}
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
